import { onMounted, ref } from 'vue'
import type { Stripe, StripeCardElement } from '@stripe/stripe-js'

import { createCardElement, getStripe } from '@/shared/services/stripe'
import {
  BillingQueryDocument,
  useUpdateBillingInformationMutation,
  type BillingQueryQuery,
} from '@/shared/graphql/legacy/generated'

export function useAddPaymentMethod() {
  const { mutate: updateBillingInformation } =
    useUpdateBillingInformationMutation()

  const stripe = ref<Stripe | null>(null)
  const card = ref<StripeCardElement | null>(null)
  const loading = ref(false)
  const errorMessage = ref('')

  onMounted(async () => {
    stripe.value = await getStripe()
    if (!stripe.value) return

    card.value = createCardElement(stripe.value)
    card.value.on('change', () => {
      errorMessage.value = ''
    })
    card.value.mount('#card')
  })

  const save = async () => {
    if (!stripe.value || !card.value) return false

    loading.value = true
    errorMessage.value = ''

    try {
      const result = await stripe.value.createPaymentMethod({
        type: 'card',
        card: card.value,
      })

      if (result.error) {
        errorMessage.value = result.error.message || 'Something went wrong'
        return false
      }

      await updateBillingInformation(
        { paymentMethodId: result.paymentMethod.id },
        {
          update(cache) {
            cache.updateQuery<BillingQueryQuery>(
              { query: BillingQueryDocument },
              (current) => {
                if (!current) return current
                return {
                  ...current,
                  billing: {
                    ...current.billing,
                    paymentMethod: {
                      __typename: 'PaymentMethod',
                      card: {
                        __typename: 'Card',
                        last4: result.paymentMethod.card?.last4 || '',
                        type: result.paymentMethod.card?.brand || '',
                      },
                      requiresAction: null,
                    },
                  },
                }
              }
            )
          },
        }
      )

      return true
    } catch (error) {
      errorMessage.value =
        error instanceof Error ? error.message : 'Something went wrong'
      return false
    } finally {
      loading.value = false
    }
  }

  return { loading, errorMessage, save }
}
